/**
 * FullscreenViewer.js
 * Полноэкранный просмотр изображения с pinch-zoom, pan и wheel-zoom.
 * Вынесен из PhotosSection для переиспользования в других компонентах.
 *
 * Props:
 *   src     — URL изображения
 *   onClose — callback при закрытии (Escape / кнопка ✕)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export default function FullscreenViewer({ src, onClose }) {
  const { t } = useTranslation();
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  const lastDist  = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const clampOffset = useCallback((ox, oy, sc) => {
    if (sc <= 1) return { x: 0, y: 0 };
    return { x: ox, y: oy };
  }, []);

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragging.current = true;
    dragStart.current = { x: e.clientX - offsetRef.current.x, y: e.clientY - offsetRef.current.y };
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const nx = e.clientX - dragStart.current.x;
    const ny = e.clientY - dragStart.current.y;
    const clamped = clampOffset(nx, ny, scale);
    offsetRef.current = clamped;
    setOffset(clamped);
  };
  const onMouseUp = () => { dragging.current = false; };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setScale(s => {
      const ns = Math.min(8, Math.max(1, s + delta));
      if (ns <= 1) { offsetRef.current = { x: 0, y: 0 }; setOffset({ x: 0, y: 0 }); }
      return ns;
    });
  };

  const onDblClick = (e) => {
    e.stopPropagation();
    if (scale > 1) {
      setScale(1); setOffset({ x: 0, y: 0 }); offsetRef.current = { x: 0, y: 0 };
    } else {
      setScale(2.5);
    }
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastDist.current = Math.hypot(dx, dy);
    } else if (e.touches.length === 1 && scale > 1) {
      dragging.current = true;
      dragStart.current = { x: e.touches[0].clientX - offsetRef.current.x, y: e.touches[0].clientY - offsetRef.current.y };
    }
  };
  const onTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2 && lastDist.current !== null) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / lastDist.current;
      lastDist.current = dist;
      setScale(s => {
        const ns = Math.min(8, Math.max(1, s * ratio));
        if (ns <= 1) { offsetRef.current = { x: 0, y: 0 }; setOffset({ x: 0, y: 0 }); }
        return ns;
      });
    } else if (e.touches.length === 1 && dragging.current) {
      const nx = e.touches[0].clientX - dragStart.current.x;
      const ny = e.touches[0].clientY - dragStart.current.y;
      const clamped = clampOffset(nx, ny, scale);
      offsetRef.current = clamped;
      setOffset(clamped);
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) lastDist.current = null;
    if (e.touches.length === 0) dragging.current = false;
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: '#000',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: scale > 1 ? 'grab' : 'zoom-in',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onDoubleClick={onDblClick}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        style={{
          maxWidth: '100vw', maxHeight: '100vh',
          transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
          transformOrigin: 'center center',
          transition: dragging.current ? 'none' : 'transform 0.1s ease',
          pointerEvents: 'none',
          display: 'block',
        }}
      />

      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: 'fixed', top: 'max(16px, env(safe-area-inset-top, 16px))', right: 'max(16px, env(safe-area-inset-right, 16px))',
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
          color: 'white', fontSize: 20, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(6px)', zIndex: 3001,
        }}
        title={t('photos.closeEsc')}
      >✕</button>

      {scale === 1 && (
        <div style={{
          position: 'fixed', bottom: 'max(20px, env(safe-area-inset-bottom, 20px))', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.5)', borderRadius: 20,
          padding: '5px 16px', fontSize: 12, color: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(4px)', pointerEvents: 'none',
        }}>
          {t('photos.zoomHint')}
        </div>
      )}

      {scale > 1 && (
        <div style={{
          position: 'fixed', bottom: 'max(20px, env(safe-area-inset-bottom, 20px))', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.5)', borderRadius: 20,
          padding: '5px 16px', fontSize: 13, color: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(4px)', pointerEvents: 'none',
        }}>
          {Math.round(scale * 100)}% · {t('photos.zoomReset')}
        </div>
      )}
    </div>
  );
}
